import React, {FormEvent, useEffect, useRef, useState} from 'react'
import {checkDraftExistsByMobile, sendWizardOtp, verifyWizardOtp} from '../../api'
import {OTPInput} from '../../../common/component/OTPInput'
import ApplicationResponsiveModal from '../common/ApplicationResponsiveModal'
import {useRetryCountdown} from '../common/useRetryCountdown'
import {
  AU_MOBILE_REGEX,
  formatAustralianMobile,
  formatAustralianMobileInput,
  normalizeMobile,
} from '../renter-home/renterHomeUtils'

type DooraCardSignInModalProps = {
  onClose: () => void
  onSuccess: (sessionId: string) => void
}

type OtpFormValues = {
  otpCode: string
}

const DooraCardSignInModal: React.FC<DooraCardSignInModalProps> = ({onClose, onSuccess}) => {
  const [stage, setStage] = useState<'mobile' | 'otp'>('mobile')
  const [mobileNumber, setMobileNumber] = useState('')
  const [otpCode, setOtpCode] = useState('')
  const [message, setMessage] = useState<string | null>(null)
  const [isSendingCode, setIsSendingCode] = useState(false)
  const [isVerifyingCode, setIsVerifyingCode] = useState(false)
  const sendRetry = useRetryCountdown()
  const verifyRetry = useRetryCountdown()
  const mobileInputRef = useRef<HTMLInputElement | null>(null)

  const normalizedMobile = normalizeMobile(mobileNumber)
  const isMobileValid = AU_MOBILE_REGEX.test(normalizedMobile)
  const isOtpComplete = otpCode.trim().length === 6

  const getMobileValidationMessage = (value: string) => {
    const digits = normalizeMobile(value)

    if (!digits) {
      return null
    }

    if (!AU_MOBILE_REGEX.test(digits)) {
      return 'Enter a valid Australian mobile (e.g. 0412 345 678).'
    }

    return null
  }

  useEffect(() => {
    const focusTimer = window.setTimeout(() => {
      mobileInputRef.current?.focus()
    }, 50)

    return () => {
      window.clearTimeout(focusTimer)
    }
  }, [stage])

  const handleMobileChange = (value: string) => {
    const formattedValue = formatAustralianMobileInput(value)
    setMobileNumber(formattedValue)
    setMessage(getMobileValidationMessage(formattedValue))
  }

  const handleSendCode = async (event?: FormEvent) => {
    event?.preventDefault()
    if (sendRetry.seconds > 0 || verifyRetry.seconds > 0) return

    if (!isMobileValid || isSendingCode) {
      setMessage('Enter a valid Australian mobile number.')
      return
    }

    setIsSendingCode(true)
    setMessage(null)
    setOtpCode('')

    try {
      const existingDraft = await checkDraftExistsByMobile(normalizedMobile)
      if (!existingDraft.exists) {
        setMessage('No Doora Card was found for that mobile number. Build your card first.')
        return
      }

      const result = await sendWizardOtp({mobileNumber: normalizedMobile})
      sendRetry.start(result.retryAfterSeconds)
      if (!result.sent) {
        setMessage(result.message ?? 'Unable to send a code right now.')
        return
      }

      setMobileNumber(formatAustralianMobile(normalizedMobile))
      setStage('otp')
    } catch {
      setMessage('Unable to send a code right now.')
    } finally {
      setIsSendingCode(false)
    }
  }

  const handleVerifyCode = async (event?: FormEvent) => {
    event?.preventDefault()
    if (verifyRetry.seconds > 0) return

    if (!isOtpComplete || isVerifyingCode) {
      setMessage('Enter the 6-digit code.')
      return
    }

    setIsVerifyingCode(true)
    setMessage(null)

    try {
      const result = await verifyWizardOtp({
        mobileNumber: normalizedMobile,
        otpCode: otpCode.trim(),
        action: 'resume',
      })

      if (!result.verified || !result.sessionId) {
        verifyRetry.start(result.retryAfterSeconds)
        if (result.isLockedOut) sendRetry.start(result.retryAfterSeconds)
        if (result.requiresNewCode) setStage('mobile')
        setMessage(result.failureCode ?? 'Unable to verify that code.')
        return
      }

      onSuccess(result.sessionId)
    } catch {
      setMessage('Unable to verify that code right now.')
    } finally {
      setIsVerifyingCode(false)
    }
  }

  const handleUseDifferentNumber = () => {
    setStage('mobile')
    setOtpCode('')
    setMessage(null)
  }

  return (
    <ApplicationResponsiveModal
      ariaLabelledBy='application-signin-title'
      closeAriaLabel='Close sign in'
      onClose={onClose}
      modalClassName='application-signin-modal'
      closeButtonClassName='application-signin-modal-close'
      hideCloseButtonOnMobile
      mobileAnimationMs={380}
      swipeTarget='sheet'
    >
      {stage === 'mobile' ? (
        <form onSubmit={handleSendCode} noValidate>
          <h2 id='application-signin-title'>Sign in</h2>
          <p className='application-signin-modal-subtitle'>
            We'll text you a 6-digit code to confirm it's you.
          </p>

          <label className='application-signin-field-label' htmlFor='doora-card-mobile'>
            Mobile Number
          </label>
          <input
            id='doora-card-mobile'
            ref={mobileInputRef}
            className={`application-signin-input ${message && !isMobileValid ? 'is-invalid' : ''}`}
            placeholder='0412 345 678'
            inputMode='tel'
            autoComplete='tel'
            value={mobileNumber}
            disabled={isSendingCode}
            onChange={(event) => handleMobileChange(event.target.value)}
          />

          {message && <div className='application-signin-message'>{message}</div>}

          <button
            type='submit'
            className='btn application-landing-cta application-signin-submit'
            disabled={!isMobileValid || isSendingCode || sendRetry.seconds > 0 || verifyRetry.seconds > 0}
          >
            {isSendingCode ? 'Sending...' : sendRetry.seconds > 0 ? `Try again in ${sendRetry.seconds}s` : 'Send code'}
          </button>
        </form>
      ) : (
        <form onSubmit={handleVerifyCode} noValidate>
          <h2 id='application-signin-title' className='text-center'>
            Check your texts
          </h2>
          <p className='application-signin-modal-subtitle text-center'>
            We sent a 6-digit code to
            <br />
            <strong>{formatAustralianMobile(normalizedMobile)}</strong>
          </p>

          <OTPInput<OtpFormValues>
            key={normalizedMobile}
            name='otpCode'
            onChange={(value) => {
              setOtpCode(value)
              setMessage(null)
            }}
            disabled={isVerifyingCode}
            className='application-signin-otp'
          />

          {message && <div className='application-signin-message text-center'>{message}</div>}

          <button
            type='button'
            className='btn btn-link application-signin-different-number text-center'
            onClick={handleUseDifferentNumber}
          >
            Use a different number
          </button>

          <button
            type='submit'
            className='btn application-landing-cta application-signin-submit'
            disabled={!isOtpComplete || isVerifyingCode || verifyRetry.seconds > 0}
          >
            {isVerifyingCode ? 'Checking...' : verifyRetry.seconds > 0 ? `Try again in ${verifyRetry.seconds}s` : 'Continue'}
          </button>
        </form>
      )}
    </ApplicationResponsiveModal>
  )
}

export default DooraCardSignInModal
